import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Camera, Loader2, ImageOff, ChevronRight, ArrowUp, ArrowDown, Sparkles } from 'lucide-react';
import { PageHeader } from './MassTimes';
import EditableText from '../components/EditableText';
import LinkifiedText from '../components/LinkifiedText';
import { CardControls, AddButton } from '../components/AdminControls';
import EditDialog from '../components/EditDialog';
import ConfirmDialog from '../components/ConfirmDialog';
import { api } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';

const INTRO_SLUG = 'im-new-intro';
const INTRO_CONTENT_KEY = 'im_new_intro_body';
const INTRO_DEFAULT_BODY = `Welcome! Whether you've just arrived in our community, are exploring the Catholic Faith for the first time, or are returning after time away, we're so glad you're here.

Below you'll find gentle next steps to help you feel at home at All Saints Catholic Parish. Take your time — there is no pressure and no obligation.`;

const TILE_FIELDS = [
  { name: 'title', label: 'Title', required: true, placeholder: 'How Do I Become a Catholic?' },
  { name: 'description', label: 'Description', type: 'textarea', required: true, rows: 4 },
  {
    name: 'learn_more_url',
    label: 'Learn More link',
    required: true,
    placeholder: '/parish-life/becoming-catholic or https://…',
    help: 'Use an internal path starting with "/" (opens in-app) or a full https:// URL (opens in a new tab).',
  },
];

// Intro banner image with admin upload/remove controls.
const IntroImage = () => {
  const { isAdmin } = useAuth();
  const { toast } = useToast();
  const [meta, setMeta] = useState({ exists: false, updated_at: 0 });
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    let alive = true;
    api.getPageImageMeta(INTRO_SLUG)
      .then(r => { if (alive) setMeta(r.data); })
      .catch(() => { if (alive) setMeta({ exists: false, updated_at: 0 }); });
    return () => { alive = false; };
  }, []);

  const src = meta.exists ? api.pageImageUrl(INTRO_SLUG, meta.updated_at) : '';

  const onFile = async (file) => {
    if (!file) return;
    if (!/^image\/(jpeg|png|webp)$/i.test(file.type)) {
      toast({ title: 'Please choose a JPEG, PNG, or WebP image', variant: 'destructive' });
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast({ title: 'Image is larger than 10 MB', variant: 'destructive' });
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.uploadPageImage(INTRO_SLUG, fd);
      setMeta({ exists: true, updated_at: data.updated_at });
      toast({ title: 'Photo uploaded' });
    } catch (e) {
      toast({ title: 'Upload failed', description: e?.response?.data?.detail || String(e), variant: 'destructive' });
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const onRemove = async () => {
    setUploading(true);
    try {
      await api.deletePageImage(INTRO_SLUG);
      setMeta({ exists: false, updated_at: 0 });
      toast({ title: 'Photo removed' });
    } catch (e) {
      toast({ title: 'Remove failed', description: e?.response?.data?.detail || String(e), variant: 'destructive' });
    } finally {
      setUploading(false);
    }
  };

  // Public visitors without an uploaded intro image see no empty container.
  if (!src && !isAdmin) return null;

  return (
    <div
      className="relative w-full aspect-[16/9] md:aspect-[21/9] overflow-hidden"
      data-testid="im-new-intro-image"
    >
      {src ? (
        <img src={src} alt="Welcome" className="w-full h-full object-cover" />
      ) : (
        <div className="w-full h-full flex flex-col items-center justify-center bg-[#faf7f0] border border-dashed border-[#c8b785] text-[#a68a5b] gap-3">
          <Sparkles size={26} />
          <span className="text-sm tracking-[0.15em] uppercase">
            Upload a welcome photo
          </span>
        </div>
      )}
      {isAdmin && (
        <>
          <input
            ref={inputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            className="hidden"
            onChange={(e) => onFile(e.target.files?.[0])}
            data-testid="im-new-intro-image-input"
          />
          <div className="absolute top-3 right-3 flex gap-2">
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              disabled={uploading}
              data-testid="im-new-intro-change-photo"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white/95 hover:bg-white text-[#3a2359] text-xs font-medium shadow disabled:opacity-70"
            >
              {uploading
                ? <><Loader2 size={12} className="animate-spin" /> Uploading…</>
                : <><Camera size={12} /> {meta.exists ? 'Change' : 'Upload'} photo</>}
            </button>
            {meta.exists && (
              <button
                type="button"
                onClick={onRemove}
                disabled={uploading}
                data-testid="im-new-intro-remove-photo"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white/95 hover:bg-white text-[#8a2b1a] text-xs font-medium shadow disabled:opacity-70"
              >
                <ImageOff size={12} /> Remove
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
};

// Single tile card — image + title + description + Learn More button.
const TileCard = ({
  tile,
  isAdmin,
  uploading,
  onEdit,
  onDelete,
  onFileChosen,
  onRemoveImage,
  onMoveUp,
  onMoveDown,
  canMoveUp,
  canMoveDown,
}) => {
  const inputRef = useRef(null);
  const src = api.imNewTileImageAbs(tile.image_url);
  const isInternal = typeof tile.learn_more_url === 'string' && tile.learn_more_url.startsWith('/');

  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    if (f && onFileChosen) onFileChosen(tile, f);
    if (inputRef.current) inputRef.current.value = '';
  };

  return (
    <article
      className="relative group bg-white border border-[#e9e0cc] hover:border-[#3a2359] hover:shadow-sm overflow-hidden flex flex-col"
      data-testid={`im-new-tile-${tile.id}`}
    >
      {src ? (
        <div className="relative w-full aspect-[16/10] overflow-hidden bg-[#f2ead6]">
          <img
            src={src}
            alt={tile.title}
            className="w-full h-full object-cover group-hover:scale-[1.03]"
            style={{ transition: 'transform 700ms ease' }}
          />
          {isAdmin && (
            <div className="absolute top-2 left-2 flex gap-2">
              <button
                type="button"
                onClick={() => inputRef.current?.click()}
                disabled={uploading}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-white/95 hover:bg-white text-[#3a2359] text-[11px] font-medium shadow disabled:opacity-70"
                data-testid={`im-new-tile-change-photo-${tile.id}`}
              >
                {uploading ? <><Loader2 size={12} className="animate-spin" /> Uploading…</> : <><Camera size={12} /> Change</>}
              </button>
              <button
                type="button"
                onClick={() => onRemoveImage && onRemoveImage(tile)}
                disabled={uploading}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-white/95 hover:bg-white text-[#8a2b1a] text-[11px] font-medium shadow disabled:opacity-70"
                data-testid={`im-new-tile-remove-photo-${tile.id}`}
              >
                <ImageOff size={12} /> Remove
              </button>
            </div>
          )}
        </div>
      ) : (
        isAdmin ? (
          <div className="w-full aspect-[16/10] bg-[#f7f0dd] border-b border-[#e9e0cc] flex items-center justify-center">
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              disabled={uploading}
              className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-[#e9e0cc] hover:border-[#3a2359] text-[#3a2359] text-xs font-medium disabled:opacity-70"
              data-testid={`im-new-tile-add-photo-${tile.id}`}
            >
              {uploading ? <><Loader2 size={14} className="animate-spin" /> Uploading…</> : <><Camera size={14} /> Add a photo</>}
            </button>
          </div>
        ) : (
          <div className="w-full aspect-[16/10] bg-[#f2ead6] flex items-center justify-center text-[#a68a5b]">
            <Sparkles size={22} />
          </div>
        )
      )}

      <div className="p-6 flex-1 flex flex-col">
        {isAdmin && (onEdit || onDelete) && (
          <CardControls onEdit={onEdit} onDelete={onDelete} />
        )}
        <h3 className="font-display text-2xl text-[#2a1a45] mb-2 leading-tight">{tile.title}</h3>
        <LinkifiedText
          as="p"
          text={tile.description}
          className="text-sm text-[#5a534a] leading-relaxed whitespace-pre-wrap flex-1"
        />

        <div className="mt-6">
          {isInternal ? (
            <Link
              to={tile.learn_more_url}
              className="inline-flex items-center gap-1.5 px-4 py-2 bg-[#3a2359] text-white text-sm hover:bg-[#4d2f75]"
              data-testid={`im-new-tile-learn-more-${tile.id}`}
            >
              Learn More <ChevronRight size={16} />
            </Link>
          ) : (
            <a
              href={tile.learn_more_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 px-4 py-2 bg-[#3a2359] text-white text-sm hover:bg-[#4d2f75]"
              data-testid={`im-new-tile-learn-more-${tile.id}`}
            >
              Learn More <ChevronRight size={16} />
            </a>
          )}
        </div>

        {isAdmin && (onMoveUp || onMoveDown) && (
          <div className="mt-4 pt-3 border-t border-[#f2ead6] flex items-center justify-between gap-2">
            <span className="text-[10px] tracking-[0.2em] uppercase text-[#a68a5b] font-medium">Reorder</span>
            <div className="flex gap-1.5">
              <button
                type="button"
                onClick={() => onMoveUp && onMoveUp(tile)}
                disabled={!canMoveUp}
                data-testid={`im-new-tile-move-up-${tile.id}`}
                aria-label="Move up"
                className="inline-flex items-center justify-center w-8 h-8 border border-[#3a2359] text-[#3a2359] bg-white hover:bg-[#3a2359] hover:text-white disabled:opacity-30 disabled:hover:bg-white disabled:hover:text-[#3a2359] disabled:cursor-not-allowed"
              >
                <ArrowUp size={14} />
              </button>
              <button
                type="button"
                onClick={() => onMoveDown && onMoveDown(tile)}
                disabled={!canMoveDown}
                data-testid={`im-new-tile-move-down-${tile.id}`}
                aria-label="Move down"
                className="inline-flex items-center justify-center w-8 h-8 border border-[#3a2359] text-[#3a2359] bg-white hover:bg-[#3a2359] hover:text-white disabled:opacity-30 disabled:hover:bg-white disabled:hover:text-[#3a2359] disabled:cursor-not-allowed"
              >
                <ArrowDown size={14} />
              </button>
            </div>
          </div>
        )}
      </div>

      {isAdmin && (
        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          className="hidden"
          onChange={handleFileChange}
          data-testid={`im-new-tile-file-input-${tile.id}`}
        />
      )}
    </article>
  );
};

const ImNew = () => {
  const { isAdmin } = useAuth();
  const { toast } = useToast();
  const [tiles, setTiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [uploadingId, setUploadingId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.listImNewTiles();
      setTiles(data);
    } catch { /* silent */ }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const handleSave = async (values) => {
    if (editing?.mode === 'new') {
      const { data } = await api.createImNewTile(values);
      setTiles(prev => [...prev, data]);
      toast({ title: 'Tile added' });
    } else {
      const { data } = await api.updateImNewTile(editing.id, values);
      setTiles(prev => prev.map(t => t.id === data.id ? data : t));
      toast({ title: 'Tile updated' });
    }
  };

  const handleDelete = async () => {
    await api.deleteImNewTile(deleteTarget.id);
    setTiles(prev => prev.filter(t => t.id !== deleteTarget.id));
    toast({ title: 'Tile deleted' });
    setDeleteTarget(null);
  };

  const handleFileChosen = async (tile, file) => {
    if (!/^image\/(jpeg|png|webp)$/i.test(file.type)) {
      toast({ title: 'Please choose a JPEG, PNG, or WebP image', variant: 'destructive' });
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast({ title: 'Image is larger than 10 MB', variant: 'destructive' });
      return;
    }
    setUploadingId(tile.id);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.uploadImNewTileImage(tile.id, fd);
      setTiles(prev => prev.map(t => t.id === data.id ? data : t));
      toast({ title: 'Photo uploaded' });
    } catch (e) {
      toast({ title: 'Upload failed', description: e?.response?.data?.detail || String(e), variant: 'destructive' });
    } finally {
      setUploadingId(null);
    }
  };

  const handleRemoveImage = async (tile) => {
    setUploadingId(tile.id);
    try {
      const { data } = await api.deleteImNewTileImage(tile.id);
      setTiles(prev => prev.map(t => t.id === data.id ? data : t));
      toast({ title: 'Photo removed' });
    } catch (e) {
      toast({ title: 'Remove failed', description: e?.response?.data?.detail || String(e), variant: 'destructive' });
    } finally {
      setUploadingId(null);
    }
  };

  const moveTile = async (tile, direction) => {
    const idx = tiles.findIndex(t => t.id === tile.id);
    if (idx < 0) return;
    const swap = direction === 'up' ? idx - 1 : idx + 1;
    if (swap < 0 || swap >= tiles.length) return;
    const next = [...tiles];
    [next[idx], next[swap]] = [next[swap], next[idx]];
    setTiles(next);
    try {
      const { data } = await api.reorderImNewTiles(next.map(t => t.id));
      setTiles(data);
    } catch (e) {
      toast({ title: 'Reorder failed', description: e?.response?.data?.detail || String(e), variant: 'destructive' });
      setTiles(tiles);
    }
  };

  return (
    <>
      <PageHeader
        eyebrow="Welcome"
        title="I'm New"
        subtitle="Come as you are. Here are gentle first steps to help you feel at home."
      />

      {/* Intro image + welcome text */}
      <section className="max-w-5xl mx-auto px-6 py-14">
        <IntroImage />
        <div className="mt-10 max-w-3xl mx-auto text-center">
          <EditableText
            contentKey={INTRO_CONTENT_KEY}
            label="I'm New — Welcome Text"
            rows={10}
            align="center"
            defaultValue={INTRO_DEFAULT_BODY}
            paragraphClass="text-[15px] md:text-lg text-[#3f3a34] leading-[1.85]"
          />
        </div>
      </section>

      {/* Tiles */}
      <section className="max-w-6xl mx-auto px-6 pb-20">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-8 gap-3">
          <div>
            <p className="text-sm tracking-[0.2em] uppercase text-[#a68a5b] font-medium mb-2">Next Steps</p>
            <h2 className="font-display text-3xl md:text-4xl text-[#2a1a45]">Explore Your Journey</h2>
          </div>
          {isAdmin && (
            <AddButton
              onClick={() => setEditing({ mode: 'new' })}
              label="Add Tile"
            />
          )}
        </div>

        {loading ? (
          <p className="text-center text-[#78716c] py-8">Loading…</p>
        ) : tiles.length === 0 ? (
          <p className="text-center text-[#78716c] py-8">
            {isAdmin ? 'No tiles yet — add your first one above.' : 'More coming soon.'}
          </p>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {tiles.map((t, i) => (
              <TileCard
                key={t.id}
                tile={t}
                isAdmin={isAdmin}
                uploading={uploadingId === t.id}
                onEdit={() => setEditing(t)}
                onDelete={() => setDeleteTarget(t)}
                onFileChosen={handleFileChosen}
                onRemoveImage={handleRemoveImage}
                onMoveUp={(tile) => moveTile(tile, 'up')}
                onMoveDown={(tile) => moveTile(tile, 'down')}
                canMoveUp={i > 0}
                canMoveDown={i < tiles.length - 1}
              />
            ))}
          </div>
        )}
      </section>

      <EditDialog
        open={!!editing}
        title={editing?.mode === 'new' ? 'Add Tile' : 'Edit Tile'}
        fields={TILE_FIELDS}
        initial={editing?.mode === 'new'
          ? { title: '', description: '', learn_more_url: '' }
          : (editing || {})
        }
        onClose={() => setEditing(null)}
        onSubmit={handleSave}
      />
      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete this tile?"
        message={deleteTarget ? `“${deleteTarget.title}” will be permanently removed.` : ''}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
      />
    </>
  );
};

export default ImNew;
